import type { DatabaseEngineId } from '@/core/engines';

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { DatabaseObjectTreeNode, DatabaseObjectNodeMetadata } from '../../explorer/models/DatabaseTreeModels';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { getConnectionById, getEngineForConnection } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { getScriptProvider } from '../../scripting';
import { openReferencesTab } from '../../navigational-features/find-references/openReferencesTab';
import { buildOeRelationIdentifier } from '../../navigational-features/oeIdentifier';
import { useRenamePreviewStore } from '../../navigational-features/rename/renamePreviewStore';
import { trackNavFeature } from '../../navigational-features/analytics/trackNavFeature';
import {
  executeAction,
  type ObjectActionContext,
} from '../../shared/object-actions';
import { parseNodeId } from '../../explorer/utils/nodeIdUtils';
import type { AiTaskContext } from '../../ai-chat/launchers/launcherRegistry';
import { createAiSubmenu } from './aiSubmenuBuilder';
import { SELECT_TOP_ROW_LIMIT } from '../../scripting/selectTopRowLimit';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

/**
 * View-specific context menu action types
 */
type ViewContextMenuActionType =
  | 'refresh'
  | 'open-view'
  | 'open-definition'
  | 'script-view-as'
  | 'script-view-create'
  | 'script-view-alter'
  | 'script-view-drop'
  | 'script-view-select'
  | 'select-top-1000'
  | 'show-dependencies'
  | 'find-references'
  | 'rename-view';

const ViewActions = {
  REFRESH: 'refresh' as ViewContextMenuActionType,
  OPEN_VIEW: 'open-view' as ViewContextMenuActionType,
  OPEN_DEFINITION: 'open-definition' as ViewContextMenuActionType,
  SCRIPT_VIEW_AS: 'script-view-as' as ViewContextMenuActionType,
  SCRIPT_VIEW_CREATE: 'script-view-create' as ViewContextMenuActionType,
  SCRIPT_VIEW_ALTER: 'script-view-alter' as ViewContextMenuActionType,
  SCRIPT_VIEW_DROP: 'script-view-drop' as ViewContextMenuActionType,
  SCRIPT_VIEW_SELECT: 'script-view-select' as ViewContextMenuActionType,
  SELECT_TOP_1000: 'select-top-1000' as ViewContextMenuActionType,
  SHOW_DEPENDENCIES: 'show-dependencies' as ViewContextMenuActionType,
  FIND_REFERENCES: 'find-references' as ViewContextMenuActionType,
  RENAME_VIEW: 'rename-view' as ViewContextMenuActionType,
};

/**
 * Mapping from Object Explorer action IDs to shared action IDs
 */
const actionIdMapping: Record<string, string> = {
  [ViewActions.SELECT_TOP_1000]: 'select-top-1000',
  [ViewActions.SCRIPT_VIEW_CREATE]: 'script-create',
  [ViewActions.SCRIPT_VIEW_ALTER]: 'script-alter',
  [ViewActions.SCRIPT_VIEW_DROP]: 'script-drop',
  [ViewActions.SHOW_DEPENDENCIES]: 'view-dependencies',
};

/**
 * Create a separator item
 */
export function createSeparator(): ContextMenuAction {
  return {
    id: 'separator',
    label: '',
    isSeparator: true
  };
}

/**
 * Create a refresh action
 */
export function createRefreshAction(): ContextMenuAction {
  return {
    id: ViewActions.REFRESH,
    label: 'Refresh',
    icon: 'refresh'
  };
}

export function createOpenViewAction(): ContextMenuAction {
  return {
    id: ViewActions.OPEN_VIEW,
    label: 'Open',
    icon: 'search',
  };
}

export function createOpenDefinitionAction(): ContextMenuAction {
  return {
    id: ViewActions.OPEN_DEFINITION,
    label: 'Open definition',
    icon: 'code',
  };
}

/**
 * Create script view submenu
 */
export function createScriptViewSubmenu(): ContextMenuAction {
  return {
    id: ViewActions.SCRIPT_VIEW_AS,
    label: 'Script view as',
    icon: 'code',
    submenu: [
      {
        id: ViewActions.SCRIPT_VIEW_CREATE,
        label: 'CREATE',
        icon: 'file-plus'
      },
      {
        id: ViewActions.SCRIPT_VIEW_ALTER,
        label: 'ALTER',
        icon: 'edit'
      },
      {
        id: ViewActions.SCRIPT_VIEW_DROP,
        label: 'DROP',
        icon: 'trash'
      },
      {
        id: ViewActions.SCRIPT_VIEW_SELECT,
        label: 'SELECT',
        icon: 'search'
      }
    ]
  };
}

/**
 * Create select top 1000 rows action
 */
export function createSelectTop1000Action(): ContextMenuAction {
  return {
    id: ViewActions.SELECT_TOP_1000,
    label: `Select top ${SELECT_TOP_ROW_LIMIT} rows`,
    icon: 'table'
  };
}

/**
 * Create show dependencies action
 */
export function createShowDependenciesAction(): ContextMenuAction {
  return {
    id: ViewActions.SHOW_DEPENDENCIES,
    label: 'Show Dependencies',
    icon: 'git-branch'
  };
}

/**
 * Create find-references action — opens the NavigationalFeatures
 * Find-References tab listing every persisted object that references the
 * view. Phase-3 Slice 25.
 */
export function createFindReferencesAction(): ContextMenuAction {
  return {
    id: ViewActions.FIND_REFERENCES,
    label: 'Find references…',
    icon: 'search',
  };
}

/**
 * Create rename-view action — opens the NavigationalFeatures rename
 * preview dialog with a name-input pre-step. Phase-3 Slice 36.
 */
export function createRenameViewAction(): ContextMenuAction {
  return {
    id: ViewActions.RENAME_VIEW,
    label: 'Rename…',
    icon: 'edit',
  };
}

/**
 * Generate view-specific context menu actions.
 *
 * The "AI" submenu offers only `explain-table` (labeled "Explain this view"
 * here) — `generate-test-data` is left off because an ordinary view is not
 * updatable, so the agent has no `INSERT INTO <view>` surface to write into
 * (T12 fix round 1, I-1/M-1 — the same append-only reasoning kusto's table
 * menu uses for `generate-test-data`, applied here).
 *
 * `aiCliAvailable` / `aiCliDetectionLoading` drive the "AI" submenu's disabled
 * state (see `createAiSubmenu` in `aiSubmenuBuilder.ts`) — omitted at call
 * sites that don't render live (e.g. `databaseNodeService.ts`'s tree-build
 * pass), which the AI items render enabled; `TreeNode.tsx`'s render-time call
 * is the one that supplies them.
 */
export function generateViewContextMenuActions(
  aiCliAvailable?: boolean,
  aiCliDetectionLoading?: boolean,
): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [];

  // Copy name action
  actions.push({ id: 'copy-name', label: 'Copy view name', icon: 'clipboard' });

  // Add view-specific actions
  actions.push(createOpenViewAction());
  actions.push(createOpenDefinitionAction());
  actions.push(createScriptViewSubmenu());
  actions.push(createShowDependenciesAction());
  actions.push(createSelectTop1000Action());
  actions.push(createFindReferencesAction());
  actions.push(createRenameViewAction());
  actions.push(
    createAiSubmenu(['explain-table'], { 'explain-table': 'Explain this view' }, aiCliAvailable, aiCliDetectionLoading),
  );

  // Add separator before common actions
  actions.push(createSeparator());

  // Add common actions
  actions.push(createRefreshAction());

  return actions;
}

/**
 * Extract database name from parent folder node ID
 * Parent ID format: "views-folder<->connectionId<->database"
 */
function extractDatabaseFromParentId(parentId: string | null | undefined): string | undefined {
  if (!parentId) return undefined;
  const parts = parseNodeId(parentId);
  // Format: [folderType, connectionId, database]
  if (parts.length >= 3) {
    return parts[2];
  }
  return undefined;
}

/**
 * Build ObjectActionContext from a DatabaseObjectTreeNode for views
 */
function buildActionContext(node: DatabaseObjectTreeNode): ObjectActionContext | null {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: objectName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, engine } = getConnectionById(connectionId);

  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return null;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';
  const resolvedSchema = schema ?? defaultSchema;
  // Prefer database from parent ID, then metadata, then connection default
  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';

  return {
    connectionId,
    databaseName: dbName,
    schemaName: resolvedSchema,
    objectName,
    objectType: 'view',
    objectId: objectDetails.id,
  };
}

/**
 * Build the `AiTaskContext` for an "AI" submenu action fired from this
 * view's context menu (`explain-table` / `generate-test-data`, dispatched by
 * `handleContextMenuAction` in `DatabaseExplorer.tsx`). Reuses the same
 * connection/database/schema resolution as every other view action.
 */
export function buildViewAiTaskContext(node: DatabaseObjectTreeNode): AiTaskContext | null {
  const context = buildActionContext(node);
  if (!context) return null;
  return {
    connectionId: context.connectionId,
    database: context.databaseName,
    object: { schema: context.schemaName, name: context.objectName, kind: 'view' },
  };
}

/**
 * Handle Open View action - opens SELECT query for the view
 */
function handleOpenView(node: DatabaseObjectTreeNode): void {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: viewName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, connection, engine } = getConnectionById(connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';
  const provider = getScriptProvider(engine);
  const query = provider.generateTableScript('select', {
    database: dbName,
    schema,
    tableName: viewName,
  });

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${viewName}_OPEN-${queryNumber}`,
    type: 'query',
    content: { query },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: dbName,
    },
  });
}

/**
 * Handle Script View SELECT action
 */
function handleScriptViewSelect(node: DatabaseObjectTreeNode): void {
  const metadata = node.metadata as DatabaseObjectNodeMetadata;
  const { objectDetails } = metadata;
  const { name: viewName, schema, connectionId } = objectDetails;

  // Get database from parent folder ID (most reliable) or fall back to metadata
  const databaseFromParent = extractDatabaseFromParentId(node.parentId);
  const databaseFromMetadata = objectDetails.metadata?.database as string | undefined;

  const { activeConnection, connection, engine } = getConnectionById(connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const dbName = databaseFromParent || databaseFromMetadata || activeConnection.connection.database || '';
  const provider = getScriptProvider(engine);
  const query = provider.generateTableScript('select', {
    database: dbName,
    schema,
    tableName: viewName,
  });

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `${viewName}_SELECT-${queryNumber}`,
    type: 'query',
    content: { query },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: dbName,
    },
  });
}

/**
 * Execute a view context menu action
 *
 * Delegates to the shared object actions system where possible,
 * and handles view-explorer-specific actions locally.
 */
export function executeViewContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: {
    onRefresh?: (nodeId: string) => void;
  } = {},
  node?: DatabaseObjectTreeNode
): void {
  // Handle refresh action
  if (actionId === ViewActions.REFRESH) {
    handlers.onRefresh?.(nodeId);
    return;
  }

  if (!node) {
    toast.error({ title: 'Error', description: 'View node not found' });
    return;
  }

  // Check if this action maps to a shared action
  const sharedActionId = actionIdMapping[actionId];
  if (sharedActionId) {
    const context = buildActionContext(node);
    if (context) {
      void executeAction(sharedActionId, context);
    }
    return;
  }

  // Handle view-specific actions
  switch (actionId) {
    case ViewActions.OPEN_VIEW:
      handleOpenView(node);
      break;

    case ViewActions.OPEN_DEFINITION:
      // Open definition is the same as script-create
      {
        const context = buildActionContext(node);
        if (context) {
          void executeAction('script-create', context);
        }
      }
      break;

    case ViewActions.SCRIPT_VIEW_SELECT:
      handleScriptViewSelect(node);
      break;

    case ViewActions.FIND_REFERENCES: {
      const context = buildActionContext(node);
      if (!context) return;
      const engineId = getEngineForConnection(context.connectionId) as DatabaseEngineId;
      openReferencesTab({
        connectionId: context.connectionId,
        database: context.databaseName,
        engineId,
        identifier: buildOeRelationIdentifier(engineId, {
          name: context.objectName,
          schema: context.schemaName,
          database: context.databaseName,
        }),
        identifierKind: 'view',
        trigger: 'context-menu',
      });
      break;
    }

    case ViewActions.RENAME_VIEW: {
      const context = buildActionContext(node);
      if (!context) return;
      const engineId = getEngineForConnection(context.connectionId) as DatabaseEngineId;
      trackNavFeature({ name: 'feature:navfeatures_rename_invoke', props: { kind: 'view', source: 'context-menu' } });
      useRenamePreviewStore.getState().startNameInput({
        connectionId: context.connectionId,
        database: context.databaseName,
        engineId,
        kind: 'view',
        fromIdentifier: buildOeRelationIdentifier(engineId, {
          name: context.objectName,
          schema: context.schemaName,
          database: context.databaseName,
        }),
      });
      break;
    }

    default:
      // Ignore unknown actions (submenu headers, etc.)
      break;
  }
}
